import { Country } from 'country-state-city';
import React, { useCallback, useEffect, useState } from 'react';
import {
  FiArrowLeft,
  FiChevronLeft,
  FiEdit2,
  FiHome,
  FiMapPin,
  FiPhone,
  FiTag,
  FiUser,
} from 'react-icons/fi';
import { IoCalendarClearOutline } from 'react-icons/io5';
import { LuBrickWall } from 'react-icons/lu';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';
import AudienceViewTopCards from './AudienceViewTopCards';
import EditAudience from './EditAudience';
import EmailInitiatedTable, { EmailRecord } from './EmailInitiatedTable';

// Type-cast icons for React compatibility
const FiTagComponent = FiTag as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const LuBrickWallComponent = LuBrickWall as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiMapPinComponent = FiMapPin as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiHomeComponent = FiHome as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiEdit2Component = FiEdit2 as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiUserComponent = FiUser as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const IoCalendarClearOutlineComponent =
  IoCalendarClearOutline as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;
const FiArrowLeftComponent = FiArrowLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiPhoneComponent = FiPhone as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Interface for email history item from API
interface EmailHistoryItem {
  id: string;
  version: number;
  campaignId: string;
  batchId: string;
  attempt: number;
  contactId: string;
  recipientEmail: string;
  status: string;
  smtpCode: number | null;
  smtpReason: string | null;
  classification: string | null;
  retryAfterMs: number | null;
  lastUpdatedAt: string;
}

// Interface for contact details from API
interface ContactDetails {
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dob: string;
    gender: string;
    address: string;
    segments: string[];
    tags: string[];
    contactStatus: string;
    status: string | null;
    createdAt: string;
    updatedAt: string;
    deliveredCount: number | null;
    openCount: number | null;
    clickCount: number | null;
    bounceCount: number | null;
    lastActivityAt: string;
    lastEmailOpenedAt: string | null;
    lastClickAt: string | null;
    engagementScore: number | null;
  };
  tags: any | null;
  segments: any | null;
  subscriptions: any | null;
  'email-history'?: EmailHistoryItem[];
}

// Interface for segment name mapping
interface SegmentMap {
  [key: string]: string;
}

// Helper function to check if a string is a postal code
const isPostalCode = (str: string): boolean => {
  if (!str) return false;
  const trimmed = str.trim();
  return /^\d{5}(?:-\d{4})?$/.test(trimmed) || /^\d{6}$/.test(trimmed);
};

// Helper function to clean address by removing postal codes
const cleanAddress = (address: string): string => {
  if (!address) return '';

  const parts = address.split(',').map(part => part.trim());

  const cleanedParts = parts.filter(part => !isPostalCode(part));

  return cleanedParts.join(', ');
};

// Helper function to normalize address by removing empty/null parts
const normalizeAddress = (addressString: string): string => {
  if (!addressString) return '';
  return addressString
    .split(',')
    .map(part => part.trim())
    .filter(part => part !== '' && part.toLowerCase() !== 'null')
    .join(', ');
};

// Helper function to parse address string and extract address, city, and country
const parseAddressString = (addressString: string): { address: string; city: string; country: string } => {
  if (!addressString || addressString.trim() === '') {
    return { address: '', city: '', country: '' };
  }

  const cleanedAddress = cleanAddress(addressString);

  const parts = cleanedAddress.split(',').map(part => part.trim());

  let nonEmptyParts = parts.filter(part => part !== '' && part.toLowerCase() !== 'null');

  if (nonEmptyParts.length === 0) {
    return { address: '', city: '', country: '' };
  }

  const lastPart = nonEmptyParts[nonEmptyParts.length - 1];
  const isLastPartCountry = lastPart && (lastPart.length === 2 || lastPart.length === 3);

  const secondLastPart = nonEmptyParts.length >= 2 ? nonEmptyParts[nonEmptyParts.length - 2] : '';
  const isSecondLastPartCity = secondLastPart && secondLastPart.length > 0 && !/^\d+$/.test(secondLastPart);

  let country = '';
  let city = '';
  let address = '';

  if (isLastPartCountry) {
    country = lastPart;

    if (isSecondLastPartCity) {
      city = secondLastPart;
      const addressParts = nonEmptyParts.slice(0, nonEmptyParts.length - 2);
      address = addressParts.length > 0 ? addressParts.join(', ') : '';
    } else {
      city = '';
      const addressParts = nonEmptyParts.slice(0, nonEmptyParts.length - 1);
      address = addressParts.length > 0 ? addressParts.join(', ') : '';
    }
  } else {
    const potentialCity = nonEmptyParts[nonEmptyParts.length - 1];
    if (potentialCity && !/^\d+$/.test(potentialCity) && potentialCity.length > 2) {
      city = potentialCity;
      const addressParts = nonEmptyParts.slice(0, nonEmptyParts.length - 1);
      address = addressParts.length > 0 ? addressParts.join(', ') : '';
    } else {
      address = nonEmptyParts.join(', ');
    }
  }

  address = address.replace(/,\s*$/, '').trim();
  address = address.replace(/,{2,}/g, ',').trim();

  return { address, city, country };
};

// Helper function to get country name from country code
const getCountryName = (countryCode: string): string => {
  if (!countryCode) return '-';
  const countries = Country.getAllCountries();
  const country = countries.find(c => c.isoCode === countryCode);
  return country ? country.name : countryCode;
};

// Helper function to format date for display
const formatDateForDisplay = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');
  } catch {
    return dateString;
  }
};

// Helper function to map API status to display status
const mapStatusToDisplay = (status: string): 'Delivered' | 'Not Delivered' | 'Bounced' => {
  const statusMap: Record<string, 'Delivered' | 'Not Delivered' | 'Bounced'> = {
    'SUCCEEDED': 'Delivered',
    'FAILED': 'Not Delivered',
    'BOUNCED': 'Bounced',
    'PENDING': 'Not Delivered',
    'PROCESSING': 'Not Delivered',
  };
  return statusMap[status] || 'Not Delivered';
};

// Component to view details of a specific audience member
const ViewAudience: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [contactDetails, setContactDetails] = useState<ContactDetails | null>(null);
  const [segmentNames, setSegmentNames] = useState<SegmentMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parsedAddress, setParsedAddress] = useState<{ address: string; city: string; country: string }>({
    address: '',
    city: '',
    country: ''
  });

  // ==================== FETCH CONTACT DETAILS ====================
  const fetchContactDetails = useCallback(async () => {
    if (!id) {
      setError('Contact ID is missing');
      setLoading(false);
      return;
    }

    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/contacts/${id}/details`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setContactDetails(data);

      if (data.contact?.address) {
        const parsed = parseAddressString(data.contact.address);
        setParsedAddress(parsed);
      }

      if (data.contact?.segments && data.contact.segments.length > 0) {
        await fetchSegmentNames(data.contact.segments);
      }

      setError(null);
    } catch (error) {
      // console.error('Failed to fetch contact details:', error);
      setError('Failed to load contact details. Please try again.');
      toast.error('Failed to load contact details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // ==================== FETCH SEGMENT NAMES ====================
  const fetchSegmentNames = async (segmentIds: string[]) => {
    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token) return;

      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/segments?page=0&size=1000`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const segments = data.content || [];

      const segmentMap: SegmentMap = {};
      segments.forEach((seg: any) => {
        segmentMap[seg.id] = seg.name;
      });

      setSegmentNames(segmentMap);
    } catch (error) {
      // console.error('Failed to fetch segment names:', error);
      toast.error('Failed to load segment names. Some segment names may not display correctly.');
    }
  };

  // ==================== INITIAL LOAD ====================
  useEffect(() => {
    fetchContactDetails();
  }, [fetchContactDetails]);

  // ==================== HANDLE SAVE SUCCESS ====================
  const handleSaveSuccess = useCallback(() => {

    setTimeout(() => {
      setIsEditing(false);
      setLoading(true);
      fetchContactDetails();
    }, 1000);
  }, [fetchContactDetails]);

  // ==================== GET SEGMENT NAMES ====================
  const getSegmentNames = (segmentIds: string[]): string => {
    if (!segmentIds || segmentIds.length === 0) return '-';
    return segmentIds
      .map(id => segmentNames[id] || id)
      .join(', ');
  };

  // ==================== FORMAT DATE ====================
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // ==================== GET COUNTRY DISPLAY ====================
  const getCountryDisplay = (countryCode: string): string => {
    if (!countryCode) return '-';
    if (countryCode.length === 2 || countryCode.length === 3) {
      return getCountryName(countryCode);
    }
    return countryCode;
  };

  // ==================== GET EMAIL HISTORY DATA ====================
  const getEmailHistoryData = (): EmailRecord[] => {
    const emailHistory = contactDetails?.['email-history'];

    // If no email history or empty array, return empty array
    if (!emailHistory || emailHistory.length === 0) {
      return [];
    }

    // Map API data to table format
    return emailHistory.map((item: EmailHistoryItem) => ({
      campaign: `Campaign ${item.campaignId}`,
      fromEmail: item.recipientEmail,
      sentAt: formatDateForDisplay(item.lastUpdatedAt),
      status: mapStatusToDisplay(item.status)
    }));
  };

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <div className='p-2'>
        <button
          onClick={() => navigate('/audience')}
          className='text-orange-500 flex items-center gap-2'
        >
          <FiArrowLeftComponent /> Back
        </button>
        <div className='flex justify-center items-center h-64'>
          <div className='text-gray-500'>Loading contact details...</div>
        </div>
      </div>
    );
  }

  // ==================== ERROR STATE ====================
  if (error || !contactDetails || !contactDetails.contact) {
    return (
      <div className='p-2'>
        <button
          onClick={() => navigate('/audience')}
          className='text-orange-500 flex items-center gap-2'
        >
          <FiArrowLeftComponent /> Back
        </button>
        <div className='mt-4 p-6 bg-red-50 border border-red-200 rounded-lg'>
          <p className='text-red-600'>{error || 'Contact not found'}</p>
          <button
            onClick={() => navigate('/audience')}
            className='mt-4 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600'
          >
            Return to Audience
          </button>
        </div>
      </div>
    );
  }

  const { contact } = contactDetails;

  // ==================== AUDIENCE MEMBER OBJECT ====================
  const audienceMember = {
    id: contact.id,
    name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'N/A',
    email: contact.email || '',
    phone: contact.phone || '',
    dob: contact.dob || '',
    gender: contact.gender || '',
    address: contact.address || '',
    segments: contact.segments || [],
    tags: contact.tags || [],
    status: contact.contactStatus || 'ACTIVE',
    lifecycleStage: 'SUBSCRIBER',
    rawAddress: contact.address || '',
  };

  // ==================== GET FULL NAME ====================
  const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'N/A';

  // ==================== GET EMAIL HISTORY ====================
  const emailHistoryData = getEmailHistoryData();

  // ==================== RENDER ====================
  return (
    <div className='p-2'>
      {/* Back to Audience button */}
      <button
        onClick={() => navigate('/audience')}
        className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
        style={{ color: '#00899E', fontSize: '14px' }}
      >
        <FiChevronLeftComponent className='mr-1' /> Back to Audience
      </button>

      {/* Header */}
      <div>
        <h2 className='text-[20px] font-bold text-[#3F5265] mb-2'>
          Audience Details
        </h2>
        <p className='text-[#657584] mb-6 text-[16px]'>
          View key information and engagement insights about this audience
          member.
        </p>
      </div>

      {/* Conditional Rendering: Edit Form or Details View */}
      {isEditing ? (
        <EditAudience
          audience={audienceMember}
          onCancel={() => setIsEditing(false)}
          onSaveSuccess={handleSaveSuccess}
        />
      ) : (
        <div className='bg-white rounded-lg border border-gray-200 shadow-sm mt-6 p-6'>
          {/* Audience Info Header */}
          <div className='flex items-start justify-between'>
            <div className='flex items-center gap-4'>
              <div className='w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center text-xl font-bold text-purple-700'>
                {fullName[0]?.toUpperCase() || '?'}
              </div>
              <div>
                <h3 className='text-[18px] font-semibold text-[#3F5265]'>
                  {fullName}
                </h3>
                <p className='text-[#657584] text-[14px]'>
                  {contact.email || 'No email provided'}
                </p>
                <p className='text-[#9FA9B2] text-[12px] mt-1'>
                  Member since {formatDate(contact.createdAt)}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              className='flex items-center gap-1 text-teal-600 text-sm hover:text-teal-800'
            >
              <FiEdit2Component /> Edit
            </button>
          </div>

          <hr className='my-6 border-t' />

          {/* Audience Details Grid */}
          <div className='grid grid-cols-2 md:grid-cols-2 gap-4 mt-6 text-sm text-gray-700'>
            <div>
              <ul className='space-y-4 text-gray-600'>
                <li className='flex items-center'>
                  <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                    <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                      <FiPhoneComponent size={17} />
                    </span>
                    PHONE NUMBER
                  </span>
                  <span className='-mt-0.5'>{contact.phone || '-'}</span>
                </li>
                <li className='flex items-center'>
                  <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                    <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                      <IoCalendarClearOutlineComponent size={17} />
                    </span>
                    DATE OF BIRTH
                  </span>
                  <span className='-mt-0.5'>{formatDate(contact.dob)}</span>
                </li>
                <li className='flex items-center'>
                  <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                    <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                      <FiUserComponent size={17} />
                    </span>
                    GENDER
                  </span>
                  <span className='-mt-0.5'>
                    {contact.gender || '-'}
                  </span>
                </li>
                <li className='flex items-center'>
                  <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                    <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                      <FiHomeComponent size={17} />
                    </span>
                    ADDRESS
                  </span>
                  <span className='-mt-0.5'>
                    {parsedAddress.address || normalizeAddress(contact.address) || '-'}
                  </span>
                </li>
              </ul>
            </div>
            <div>
              <ul className='space-y-4 text-gray-600'>
                <li className='flex items-center'>
                  <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                    <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                      <FiMapPinComponent size={17} />
                    </span>
                    CITY
                  </span>
                  <span className='-mt-0.5'>
                    {parsedAddress.city || '-'}
                  </span>
                </li>
                <li className='flex items-center'>
                  <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                    <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                      <FiMapPinComponent size={17} />
                    </span>
                    COUNTRY
                  </span>
                  <span className='-mt-0.5'>
                    {getCountryDisplay(parsedAddress.country)}
                  </span>
                </li>
                <li className='flex items-center'>
                  <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                    <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                      <LuBrickWallComponent size={17} />
                    </span>
                    SEGMENTS
                  </span>
                  <span className='-mt-0.5'>
                    {contact.segments && contact.segments.length > 0
                      ? getSegmentNames(contact.segments)
                      : '-'}
                  </span>
                </li>
                <li className='flex items-center'>
                  <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                    <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                      <FiTagComponent size={17} />
                    </span>
                    TAGS
                  </span>
                  <span className='-mt-0.5'>
                    {contact.tags && contact.tags.length > 0
                      ? contact.tags.join(', ')
                      : '-'}
                  </span>
                </li>
                <li className='flex items-center'>
                  <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                    <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                      <FiUserComponent size={17} />
                    </span>
                    LAST UPDATED
                  </span>
                  <span className='-mt-0.5'>
                    {formatDate(contact.updatedAt)}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Engagement Metrics Cards - NEW */}
      <AudienceViewTopCards
        metrics={{
          deliveredCount: contact.deliveredCount,
          openCount: contact.openCount,
          clickCount: contact.clickCount,
          bounceCount: contact.bounceCount,
          engagementScore: contact.engagementScore,
          lastActivityAt: contact.lastActivityAt,
          lastEmailOpenedAt: contact.lastEmailOpenedAt,
          lastClickAt: contact.lastClickAt,
        }}
      />

      {/* Email History Table */}
      <EmailInitiatedTable
        records={emailHistoryData}
        emptyMessage="No email history found for this contact"
      />

      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default ViewAudience;